(function () {
  'use strict';

  var BANNER_SELECTOR = '[data-akiki-promotion="true"]';
  var visibilityRatios = new WeakMap();
  var visibilityTimers = new WeakMap();
  var observedElements = new WeakSet();
  var viewedBanners = new Set();

  function isMobileCreative() {
    return window.matchMedia('(max-width: 699px)').matches;
  }

  function isBannerActive(element) {
    if (!element || !element.isConnected) {
      return false;
    }

    var style = window.getComputedStyle(element);

    if (
      style.display === 'none' ||
      style.visibility === 'hidden' ||
      Number(style.opacity) === 0 ||
      element.hidden ||
      element.getAttribute('aria-hidden') === 'true'
    ) {
      return false;
    }

    /*
     * The Akiki Prestige theme uses is-selected for the currently
     * active slideshow slide. Hidden slides must not receive views.
     */
    if (
      element.classList.contains('slideshow__slide') &&
      !element.classList.contains('is-selected')
    ) {
      return false;
    }

    return true;
  }

  function getBannerData(element) {
    var mobile = isMobileCreative();

    var creativeName = mobile
      ? element.getAttribute('data-creative-mobile')
      : element.getAttribute('data-creative-desktop');

    if (!creativeName) {
      creativeName =
        element.getAttribute('data-creative-desktop') ||
        element.getAttribute('data-creative-mobile') ||
        '';
    }

    var rawDestination =
      element.getAttribute('data-destination-url') ||
      element.getAttribute('href') ||
      '';

    var destinationUrl = rawDestination;
    var destinationPath = rawDestination;

    try {
      var parsedDestination = new URL(
        rawDestination,
        window.location.href
      );

      destinationUrl = parsedDestination.href;
      destinationPath =
        parsedDestination.pathname + parsedDestination.search;
    } catch (error) {
      // Retain the original value.
    }

    return {
      promotion_id:
        element.getAttribute('data-promotion-id') || '',

      promotion_name:
        element.getAttribute('data-promotion-name') || '',

      creative_name: creativeName,

      creative_slot:
        element.getAttribute('data-creative-slot') || '',

      banner_type:
        element.getAttribute('data-banner-type') || '',

      device_variant: mobile ? 'mobile' : 'desktop',

      destination_url: destinationUrl,
      destination_path: destinationPath,

      source_page:
        window.location.pathname + window.location.search,

      section_id:
        element.getAttribute('data-section-id') || '',

      block_id:
        element.getAttribute('data-block-id') || ''
    };
  }

  function isValidBannerData(data) {
    return Boolean(
      data.promotion_id &&
      data.promotion_name &&
      data.creative_name &&
      data.creative_slot
    );
  }

  function pushPromotionEvent(eventName, data) {
    window.dataLayer = window.dataLayer || [];

    /*
     * Clear the previous ecommerce object so GTM doesn't reuse
     * another ecommerce event's items array.
     */
    window.dataLayer.push({
      ecommerce: null
    });

    window.dataLayer.push({
      event: eventName,

      promotion_id: data.promotion_id,
      promotion_name: data.promotion_name,
      creative_name: data.creative_name,
      creative_slot: data.creative_slot,

      banner_type: data.banner_type,
      device_variant: data.device_variant,
      destination_url: data.destination_url,
      destination_path: data.destination_path,
      source_page: data.source_page,
      section_id: data.section_id,
      block_id: data.block_id,

      ecommerce: {
        items: [
          {
            item_id:
              data.destination_path || data.promotion_id,

            item_name: data.promotion_name,

            promotion_id: data.promotion_id,
            promotion_name: data.promotion_name,
            creative_name: data.creative_name,
            creative_slot: data.creative_slot
          }
        ]
      }
    });
  }

  function pushLandingEvent(data) {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: 'banner_landing',

      promotion_id: data.promotion_id,
      promotion_name: data.promotion_name,
      creative_name: data.creative_name,
      creative_slot: data.creative_slot,

      banner_type: data.banner_type,
      device_variant: data.device_variant,
      destination_url: data.destination_url,
      source_page: data.source_page,
      landing_page:
        window.location.pathname + window.location.search,

      section_id: data.section_id,
      block_id: data.block_id
    });
  }

  function getViewKey(element, data) {
    return [
      data.section_id,
      data.block_id,
      data.device_variant
    ].join('|');
  }

  function cancelViewTimer(element) {
    var timer = visibilityTimers.get(element);

    if (timer) {
      window.clearTimeout(timer);
      visibilityTimers.delete(element);
    }
  }

  function evaluateVisibility(element) {
    cancelViewTimer(element);

    var ratio = visibilityRatios.get(element) || 0;

    if (ratio < 0.5 || !isBannerActive(element)) {
      return;
    }

    var bannerData = getBannerData(element);

    if (!isValidBannerData(bannerData)) {
      return;
    }

    var viewKey = getViewKey(element, bannerData);

    if (viewedBanners.has(viewKey)) {
      return;
    }

    var timer = window.setTimeout(function () {
      var latestRatio = visibilityRatios.get(element) || 0;

      if (
        latestRatio < 0.5 ||
        !isBannerActive(element)
      ) {
        return;
      }

      var latestData = getBannerData(element);

      if (!isValidBannerData(latestData)) {
        return;
      }

      viewedBanners.add(
        getViewKey(element, latestData)
      );

      pushPromotionEvent(
        'view_promotion',
        latestData
      );
    }, 1000);

    visibilityTimers.set(element, timer);
  }

  var intersectionObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        visibilityRatios.set(
          entry.target,
          entry.intersectionRatio
        );

        evaluateVisibility(entry.target);
      });
    },
    {
      threshold: [0, 0.5, 1]
    }
  );

  function observeBanner(element) {
    if (
      !element ||
      observedElements.has(element)
    ) {
      return;
    }

    observedElements.add(element);
    intersectionObserver.observe(element);
  }

  function scanForBanners(root) {
    if (
      root instanceof Element &&
      root.matches(BANNER_SELECTOR)
    ) {
      observeBanner(root);
    }

    var banners =
      root.querySelectorAll instanceof Function
        ? root.querySelectorAll(BANNER_SELECTOR)
        : [];

    banners.forEach(observeBanner);
  }

  document.addEventListener(
    'click',
    function (event) {
      if (!(event.target instanceof Element)) {
        return;
      }

      var banner =
        event.target.closest(BANNER_SELECTOR);

      if (!banner) {
        return;
      }

      var bannerData = getBannerData(banner);

      if (!isValidBannerData(bannerData)) {
        return;
      }

      pushPromotionEvent(
        'select_promotion',
        bannerData
      );

      try {
        window.sessionStorage.setItem(
          'akiki_banner_pending_landing',
          JSON.stringify({
            promotion_id: bannerData.promotion_id,
            promotion_name: bannerData.promotion_name,
            creative_name: bannerData.creative_name,
            creative_slot: bannerData.creative_slot,
            banner_type: bannerData.banner_type,
            device_variant: bannerData.device_variant,
            destination_url: bannerData.destination_url,
            destination_path: bannerData.destination_path,
            source_page: bannerData.source_page,
            section_id: bannerData.section_id,
            block_id: bannerData.block_id,
            clicked_at: Date.now()
          })
        );

        /*
         * Kept for later post-click attribution/reporting.
         * sessionStorage clears when the browser session ends.
         */
        window.sessionStorage.setItem(
          'akiki_last_banner_promotion',
          JSON.stringify({
            promotion_id: bannerData.promotion_id,
            promotion_name: bannerData.promotion_name,
            creative_name: bannerData.creative_name,
            creative_slot: bannerData.creative_slot,
            clicked_at: Date.now()
          })
        );
      } catch (error) {
        // Tracking click still works if storage is unavailable.
      }
    },
    true
  );

  function checkBannerLanding() {
    var storedValue;

    try {
      storedValue =
        window.sessionStorage.getItem(
          'akiki_banner_pending_landing'
        );
    } catch (error) {
      return;
    }

    if (!storedValue) {
      return;
    }

    try {
      var bannerData = JSON.parse(storedValue);
      var clickAge =
        Date.now() - Number(bannerData.clicked_at || 0);

      /*
       * Landing must occur within two minutes of the click.
       */
      if (clickAge > 120000) {
        window.sessionStorage.removeItem(
          'akiki_banner_pending_landing'
        );
        return;
      }

      var destination = new URL(
        bannerData.destination_url,
        window.location.href
      );

      if (
        destination.origin === window.location.origin &&
        destination.pathname === window.location.pathname
      ) {
        pushLandingEvent(bannerData);

        window.sessionStorage.removeItem(
          'akiki_banner_pending_landing'
        );
      }
    } catch (error) {
      window.sessionStorage.removeItem(
        'akiki_banner_pending_landing'
      );
    }
  }

  function initialiseBannerTracking() {
    scanForBanners(document);
    checkBannerLanding();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initialiseBannerTracking
    );
  } else {
    initialiseBannerTracking();
  }

  /*
   * Watches for slide selection changes and dynamically
   * inserted Shopify theme editor content.
   */
  var mutationObserver = new MutationObserver(
    function (mutations) {
      mutations.forEach(function (mutation) {
        if (
          mutation.type === 'attributes' &&
          mutation.target instanceof Element &&
          mutation.target.matches(BANNER_SELECTOR)
        ) {
          evaluateVisibility(mutation.target);
        }

        mutation.addedNodes.forEach(function (node) {
          if (node instanceof Element) {
            scanForBanners(node);
          }
        });
      });
    }
  );

  mutationObserver.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: [
      'class',
      'hidden',
      'aria-hidden'
    ]
  });
})();